import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { useVenue } from '../../context/VenueContext';
import { LogOut, Menu, X, Star, HelpCircle, Lock, ChevronDown } from 'lucide-react';
import { adminApi } from '../../services/api';
import { getRoutesByGroup, ADMIN_GROUPS, ADMIN_ROUTE_MAP } from '../adminRoutes';
import { useAppConfig } from '../../context/AppConfigContext';
import VenueSelector from './VenueSelector';
import OnboardingWizard from './OnboardingWizard';
import BugReportButton from '../../components/BugReportButton';
import { TooltipProvider } from './TooltipSystem';
import AiAssistant from './AiAssistant';

function NavItem({ to, label, icon: Icon, end, onClick, isFav, onToggleFav }) {  return (
    <div className="flex items-center group/row">
      <NavLink
        to={to}
        end={end}
        onClick={onClick}
        data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 group flex-1 min-w-0 ${
            isActive
              ? 'bg-[#FDECE7] text-[#D44E20] font-medium'
              : 'text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#1A1A1A]'
          }`
        }
      >
        <Icon size={18} strokeWidth={1.5} className="transition-transform duration-200 group-hover:scale-110 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </NavLink>
      {to !== '/admin' && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFav(to); }}
          data-testid={`fav-toggle-${label.toLowerCase().replace(/\s+/g, '-')}`}
          className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-200 ${
            isFav
              ? 'text-[#F59E0B] hover:text-[#D97706]'
              : 'text-transparent group-hover/row:text-[#94A3B8] hover:!text-[#F59E0B]'
          }`}
          title={isFav ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
        >
          <Star size={14} strokeWidth={1.5} fill={isFav ? 'currentColor' : 'none'} />
        </button>
      )}
    </div>
  );
}

function SidebarContent({ adminUser, logo_url, business_name, onLogout, onNavClick, favorites, onToggleFav, onReopenWizard, enabledRoutes }) {
  const appCfg = useAppConfig();
  const location = useLocation();
  const LAZO_ONLY = ['/admin/plans', '/admin/billing'];

  // All groups COLLAPSED by default — user opens what they need. Manual
  // toggles persist in localStorage; the auto-expand-on-route logic below
  // re-opens any group that contains the page the user is currently on.
  const COLLAPSE_STORAGE_KEY = 'lazo_admin_sidebar_collapsed';
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* fall through */ }
    // First-visit default: every group collapsed.
    return ADMIN_GROUPS.reduce((acc, g) => ({ ...acc, [g]: true }), {});
  });
  const persist = (next) => {
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(next)); } catch {}
  };
  const toggleGroup = (group) => persist({ ...collapsed, [group]: !collapsed[group] });

  // When the user navigates into a route that lives inside a
  // currently-collapsed group, auto-expand that group so they always
  // see where they are. We only OPEN — never close — based on
  // location, so the user's manual collapses are preserved on the
  // current screen.
  useEffect(() => {
    setCollapsed(prev => {
      let changed = false;
      const next = { ...prev };
      ADMIN_GROUPS.forEach(group => {
        if (!prev[group]) return; // already open
        const groupRoutes = getRoutesByGroup(group);
        const containsActive = groupRoutes.some(r => {
          if (r.end) return location.pathname === r.to;
          return location.pathname === r.to || location.pathname.startsWith(r.to + '/');
        });
        if (containsActive) { next[group] = false; changed = true; }
      });
      if (changed) {
        try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(next)); } catch {}
      }
      return changed ? next : prev;
    });
  }, [location.pathname]);

  return (
    <>
      {/* Logo — Platform brand (Lazo) primary, current brand as context */}
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden shadow-md bg-[#D44E20]">
            <span className="text-white font-black text-lg tracking-tight">L</span>
          </div>
          <div className="min-w-0">
            <p className="font-bold text-[#1A1A1A] text-sm leading-tight truncate">Lazo</p>
            <p className="text-[#94A3B8] text-[10px] font-medium truncate">
              {business_name ? `Admin · ${business_name}` : 'Dashboard Admin'}
            </p>
          </div>
        </div>
      </div>

      {/* Venue Selector */}
      <VenueSelector />

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {ADMIN_GROUPS.map(group => {
          const groupRoutes = getRoutesByGroup(group)
            .filter(n => !enabledRoutes || enabledRoutes.includes(n.to))
            .filter(n => !(appCfg.single_tenant && LAZO_ONLY.includes(n.to)));
          if (groupRoutes.length === 0) return null;
          const isCollapsed = !!collapsed[group];
          return (
            <div key={group} className="mb-5" data-testid={`sidebar-group-${group.toLowerCase().replace(/\s+/g, '-')}`}>
              <button
                onClick={() => toggleGroup(group)}
                aria-expanded={!isCollapsed}
                data-testid={`sidebar-group-toggle-${group.toLowerCase().replace(/\s+/g, '-')}`}
                className="w-full flex items-center justify-between px-3 mb-2 group/header"
              >
                <span className="text-[10px] font-semibold text-[#94A3B8] uppercase tracking-wider group-hover/header:text-[#64748B] transition-colors">{group}</span>
                <ChevronDown
                  size={14}
                  strokeWidth={2}
                  className={`text-[#94A3B8] group-hover/header:text-[#64748B] transition-transform duration-200 ${isCollapsed ? '-rotate-90' : 'rotate-0'}`}
                />
              </button>
              {!isCollapsed && (
                <div className="space-y-0.5 animate-fade-in">
                  {groupRoutes.map(n => (
                    <NavItem
                      key={n.to}
                      to={n.to}
                      label={n.label}
                      icon={n.icon}
                      end={n.end}
                      onClick={onNavClick}
                      isFav={favorites.includes(n.to)}
                      onToggleFav={onToggleFav}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User info */}
      <div className="px-4 py-4 border-t border-slate-100">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-[#FDECE7] rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-[#D44E20] font-bold text-sm">{adminUser?.name?.charAt(0) || 'A'}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[#1A1A1A] text-xs font-semibold truncate">{adminUser?.name}</p>
            <p className="text-[#94A3B8] text-[10px] capitalize">{adminUser?.role}</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#64748B] hover:text-[#EF4444] hover:bg-red-50 transition-all duration-200"
        >
          <LogOut size={16} strokeWidth={1.5} /> Disconnetti
        </button>
        {onReopenWizard && (
          <button
            onClick={onReopenWizard}
            data-testid="reopen-wizard-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#64748B] hover:text-[#D44E20] hover:bg-[#FDECE7] transition-all duration-200 mt-1"
          >
            <HelpCircle size={16} strokeWidth={1.5} /> Guida introduttiva
          </button>
        )}
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { adminUser, adminLogout } = useAuth();
  const { logo_url, business_name } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [enabledRoutes, setEnabledRoutes] = useState(null);
  const [disabledMap, setDisabledMap] = useState({});
  const [stripeMode, setStripeMode] = useState('live');
  const [paypalMode, setPaypalMode] = useState('live');

  // Refresh just the payment-modes (used both at mount and after the user
  // saves a new config in AdminPaymentsConfig — see custom event below).
  const refreshPaymentModes = useCallback(() => {
    adminApi.get('/admin/integrations/payments')
      .then(r => {
        setStripeMode(r.data.stripe_mode || 'live');
        setPaypalMode(r.data.paypal_mode || 'live');
      })
      .catch(() => {});
  }, []);

  // Listen for a window-level event broadcast from AdminPaymentsConfig
  // after a successful save. Belt-and-suspenders: also re-fetch on every
  // route change so navigating between admin pages keeps the banner fresh.
  useEffect(() => {
    const handler = () => refreshPaymentModes();
    window.addEventListener('payments-config-updated', handler);
    return () => window.removeEventListener('payments-config-updated', handler);
  }, [refreshPaymentModes]);

  useEffect(() => {
    refreshPaymentModes();
  }, [location.pathname, refreshPaymentModes]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    adminApi.get('/admin/favorites')
      .then(r => setFavorites(r.data.favorites || []))
      .catch(() => {});
    // Load global feature toggles → filter sidebar
    adminApi.get('/admin/feature-toggles')
      .then(r => {
        const disabled = r.data.disabled || [];
        const disabledRoutes = disabled.map(d => d.route);
        const map = {};
        disabled.forEach(d => { map[d.route] = d; });
        setDisabledMap(map);
        const all = Object.keys(ADMIN_ROUTE_MAP);
        setEnabledRoutes(all.filter(r => !disabledRoutes.includes(r)));
      })
      .catch(() => { setEnabledRoutes(null); });
    // Check onboarding status
    adminApi.get('/admin/onboarding/status')
      .then(r => {
        if (!r.data.onboarding_completed) {
          setWizardStep(r.data.wizard_step || 0);
          setShowWizard(true);
        }
      })
      .catch(() => {});
  }, []);

  const toggleFav = useCallback((route) => {
    setFavorites(prev => {
      const next = prev.includes(route)
        ? prev.filter(r => r !== route)
        : [...prev, route];
      adminApi.put('/admin/favorites', { favorites: next }).catch(() => {});
      return next;
    });
  }, []);

  const reopenWizard = async () => {
    try { await adminApi.put('/admin/onboarding/reset'); } catch {}
    setWizardStep(0);
    setShowWizard(true);
  };

  const handleLogout = () => { adminLogout(); navigate('/admin/login'); };
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="admin-app flex min-h-dvh">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-white border-r border-slate-100 flex-col h-screen fixed left-0 top-0 z-10">
        <SidebarContent adminUser={adminUser} logo_url={logo_url} business_name={business_name} onLogout={handleLogout} onNavClick={undefined} favorites={favorites} onToggleFav={toggleFav} onReopenWizard={reopenWizard} enabledRoutes={enabledRoutes} />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={closeMobile} />
      )}

      {/* Mobile sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-72 bg-white z-50 flex flex-col transform transition-transform duration-300 md:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button onClick={closeMobile} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-[#64748B] z-10">
          <X size={16} />
        </button>
        <SidebarContent adminUser={adminUser} logo_url={logo_url} business_name={business_name} onLogout={handleLogout} onNavClick={closeMobile} favorites={favorites} onToggleFav={toggleFav} onReopenWizard={reopenWizard} enabledRoutes={enabledRoutes} />
      </aside>

      {/* Content */}
      <main className="flex-1 md:ml-64 min-h-screen overflow-y-auto">
        {/* Test-mode persistent banner — clearly warns when ANY payment system is in test/sandbox */}
        {(stripeMode === 'test' || paypalMode === 'sandbox') && (
          <button
            onClick={() => navigate('/admin/integrations/payments')}
            data-testid="payments-test-mode-banner"
            title="Clicca per gestire le modalità di pagamento"
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs md:text-sm font-bold px-4 py-2 flex items-center justify-center gap-2 hover:from-amber-600 hover:to-orange-600 transition-colors sticky top-0 z-40 shadow-md"
          >
            <span className="text-base">🧪</span>
            <span className="hidden sm:inline">MODALITÀ DI TEST ATTIVA —</span>
            <span>
              {stripeMode === 'test' && paypalMode === 'sandbox'
                ? 'Stripe TEST + PayPal SANDBOX'
                : stripeMode === 'test'
                  ? 'Stripe TEST'
                  : 'PayPal SANDBOX'}
            </span>
            <span className="hidden md:inline opacity-90">· nessun addebito reale · clicca per gestire</span>
          </button>
        )}
        {/* Mobile top bar */}
        <div className="flex md:hidden items-center gap-3 px-4 py-3 bg-white border-b border-slate-100 sticky top-0 z-30">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-[#64748B]">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-[#D44E20]">
              <span className="text-white font-black text-sm tracking-tight">L</span>
            </div>
            <span className="font-bold text-[#1A1A1A] text-sm truncate">
              Lazo
              {business_name && <span className="font-normal text-[#94A3B8] ml-1.5">· {business_name}</span>}
            </span>
          </div>
        </div>
        <Outlet context={{ disabledMap }} />
      </main>
      {/* AI Assistant floating widget */}
      <AiAssistant />
      <BugReportButton userRole="admin" userName={adminUser?.name || ''} brandName={business_name || ''} />
      {/* Disabled module overlay */}
      <DisabledModuleGate disabledMap={disabledMap} />
      {/* Onboarding wizard */}
      {showWizard && (
        <OnboardingWizard
          initialStep={wizardStep}
          onComplete={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}


function DisabledModuleGate({ disabledMap }) {
  const location = useLocation();
  const match = disabledMap[location.pathname];
  if (!match) return null;
  return (
    <div
      className="fixed inset-0 md:left-64 top-0 z-40 bg-white/95 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="module-disabled-gate"
    >
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#FDECE7] flex items-center justify-center mx-auto mb-4">
          <Lock size={28} className="text-[#D44E20]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">
          "{match.label}" è temporaneamente disabilitato
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          {match.note || 'Stiamo rifinendo questa sezione per offrirtela al meglio. Torna più tardi.'}
        </p>
        <a
          href="/admin"
          className="inline-block px-5 py-2.5 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md"
          data-testid="back-to-overview-btn"
        >
          Torna alla dashboard
        </a>
      </div>
    </div>
  );
}
